/**
 * Canonical public marketing routes used for sitemap generation and prerendering.
 *
 * What: Defines route path normalization + membership checks, plus a deterministic
 * manifest of public routes (homepage, level pages, blog listing/articles, legal pages).
 * Why: Lets the server return crawlable, route-specific HTML (when prerendered)
 * and generate sitemap.xml from a shared source of truth.
 * When: Update whenever public marketing routes are added/removed or their URL patterns change.
 */

import {
  SUBJECT_PAGES,
  SUBJECT_PAGE_PATHS,
} from '../tuitionSubjects/subjectPages.js';
import { BLOG_ROUTE_CATALOG } from '../blog/_generatedBlogRoutes.js';
// Partnership *route paths* and the membership predicate. `partnershipRoutes.ts`
// holds no value import of its own for the same client-bundle reason as
// `schoolAreas.ts` below.
import {
  isPartnershipRoutePath,
  PARTNERSHIP_ROUTE_PATHS,
} from '../partnerships/partnershipRoutes.js';
// Area *route paths* only — `schoolAreas.ts` deliberately holds no value import
// of the ~290KB school catalog, because this module ships in the client bundle.
import { AREA_ROUTE_PATHS } from '../schools/schoolAreas.js';
import { GENERATED_ROUTE_LAST_MODIFIED } from './_generatedRouteLastModified.js';

type PublicMarketingRoutePath = `/${string}`;

type PublicMarketingRouteEntry = Readonly<{
  routePath: PublicMarketingRoutePath;
  priority: number;
  lastModified: string | null;
  /**
   * Whether the build-time prerender crawl snapshots this route.
   * `false` = sitemap-only: the route is served live as the SPA shell and is
   * never prerendered (e.g. interactive demo pages that need a DB-backed
   * server and fail the prerender OG htmlContracts). Consumers that walk the
   * prerender set must filter these out; the sitemap includes them.
   */
  prerender: boolean;
}>;

/**
 * Which school dataset the prerender pipeline injects for a route.
 *
 * `area` pages list primary *and* secondary schools side by side (the catchment
 * split is the point of the page), so they receive both payloads rather than a
 * third bespoke one.
 */
type PrerenderSchoolDirectoryType = 'primary' | 'secondary' | 'area';

type BlogCatalogEntry = Readonly<{
  routePath: PublicMarketingRoutePath;
  publishDate?: string;
  updatedDate?: string;
}>;

const BLOG_POST_CATALOG_ENTRIES =
  BLOG_ROUTE_CATALOG as readonly BlogCatalogEntry[];

const PRIMARY_LEVELS = Object.freeze(['1', '2', '3', '4', '5', '6'] as const);
const SECONDARY_HUB_LEVELS = Object.freeze(['1', '2', '3', '4'] as const);
/** Secondary stream slugs (g1, g2, g3, ip) used for route building. */
export const SECONDARY_STREAMS = Object.freeze([
  'g1',
  'g2',
  'g3',
  'ip',
] as const);

const PRIMARY_HUB_PAGE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(['/primary-school']);
const SECONDARY_HUB_PAGE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(['/secondary-school']);

const PRIMARY_LEVEL_PATHS: readonly PublicMarketingRoutePath[] = Object.freeze(
  PRIMARY_LEVELS.map(level => `/primary-${level}` as PublicMarketingRoutePath)
);

const SECONDARY_HUB_LEVEL_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(
    SECONDARY_HUB_LEVELS.map(
      level => `/sec-${level}` as PublicMarketingRoutePath
    )
  );

const SECONDARY_STREAM_LEVEL_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(
    SECONDARY_HUB_LEVELS.flatMap(secLevel =>
      SECONDARY_STREAMS.map(
        stream => `/sec-${secLevel}-${stream}` as PublicMarketingRoutePath
      )
    )
  );

// IB-track Year-N pages cover the IP / pre-IB years at ACS(I) and SJI.
// Year 1 is single-stream Enriched Mathematics; Years 3-4 split into Core
// and Advanced Mathematics, both taught in parallel.
const SECONDARY_IB_LEVEL_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(['/sec-1-ib', '/sec-2-ib', '/sec-3-ib', '/sec-4-ib']);

const EXAM_LEVEL_PATHS: readonly PublicMarketingRoutePath[] = Object.freeze([
  '/psle',
  '/o-level',
  '/n-level',
  '/e-math',
  '/a-math',
  '/integrated-programme',
  '/ib',
]);

const ONLINE_TUITION_PILLAR_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(['/online-math-tuition']);

const ONLINE_TUITION_SPOKE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([
    '/online-psle-math-tuition',
    '/online-o-level-math-tuition',
    '/online-secondary-math-tuition',
  ]);

const ONLINE_TUITION_COMPARISON_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([
    '/online-or-in-person-math-tuition-singapore',
    '/online-math-tuition-options-singapore',
  ]);

const ONLINE_TUITION_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([
    ...ONLINE_TUITION_PILLAR_PATHS,
    ...ONLINE_TUITION_SPOKE_PATHS,
    ...ONLINE_TUITION_COMPARISON_PATHS,
  ]);

// Area (neighbourhood) tuition pages — `/math-tuition/<area>`. One templated
// page per catchment in `shared/schools/schoolAreas.ts`. These exist to catch
// commercial local queries ("primary math tuition woodlands") that Google
// currently answers with a school profile page at a 0% CTR.
//
// Routed and reachable (see App.tsx) but deliberately withheld from the
// sitemap and prerender crawl below — the copy is not yet good enough to
// publish. `AREA_TUITION_ROUTE_PATHS` itself stays defined and non-empty
// because `AREA_TUITION_ROUTE_PATHS_SET` still backs the prerender school-
// directory classification and the shared-mtime sentinel further down; only
// its two PUBLIC_MARKETING_ROUTE_PATHS_SET / sitemap-loop usages are removed.
// Re-add both once the copy is fixed.
const AREA_TUITION_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(AREA_ROUTE_PATHS as readonly PublicMarketingRoutePath[]);

// `/psle-math-practice` is authored and routed (see App.tsx) as the intended
// indexable front door for the adaptive practice demo, but is deliberately
// withheld from the manifest below — not in PUBLIC_LEVEL_ROUTE_PATHS, the
// sitemap entries, or the prerender crawl — until the demo pool covers more
// than one level/stream/topic. It also carries `forceNoindex` as a second
// layer (PsleMathPracticePage.tsx). Re-add a route-paths block here, mirroring
// the syllabus hubs below, once the pool has grown.

// Syllabus hubs — entry points over the existing per-level topic data.
const SYLLABUS_HUB_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(['/secondary-math-syllabus', '/primary-math-syllabus']);

const PUBLIC_LEVEL_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([
    ...PRIMARY_HUB_PAGE_PATHS,
    ...PRIMARY_LEVEL_PATHS,
    ...SECONDARY_HUB_PAGE_PATHS,
    ...SECONDARY_HUB_LEVEL_PATHS,
    ...SECONDARY_STREAM_LEVEL_PATHS,
    ...SECONDARY_IB_LEVEL_PATHS,
    ...EXAM_LEVEL_PATHS,
    ...ONLINE_TUITION_ROUTE_PATHS,
    ...SYLLABUS_HUB_ROUTE_PATHS,
  ]);

const LEGAL_ROUTE_PATHS: readonly PublicMarketingRoutePath[] = Object.freeze([
  '/terms',
  '/privacy',
]);

// E-E-A-T trust pages: rendered via JS, but Google reads these to evaluate
// "who is this company and can I trust them." Prerendering ensures crawlers
// see real content instead of the empty SPA shell.
const TRUST_ROUTE_PATHS: readonly PublicMarketingRoutePath[] = Object.freeze([
  '/about-us',
  '/contact',
]);

// Partnership welcome pages — one root-level route per community programme
// DeepThink gives free accounts to (see `shared/partnerships/partnershipRoutes.ts`).
// Indexable and prerendered: the page is the public record of the partnership,
// and the people it is written for arrive from a printed handout or a forwarded
// message, so it has to answer to a search for the partner's name.
const PARTNERSHIP_WELCOME_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(PARTNERSHIP_ROUTE_PATHS as readonly PublicMarketingRoutePath[]);

// Interactive product demo pages (registered in App.tsx). These are real app
// routes, NOT prerendered — they need a live DB-backed server and fail the
// prerender OG htmlContracts. They are listed in the sitemap for discoverability
// only, at the lowest priority: demo pages carry near-zero organic-search value,
// so they must not compete with the marketing/level pages for crawl attention.
// Intentionally NOT in `PUBLIC_MARKETING_ROUTE_PATHS_SET` — they serve the SPA
// shell (200), never prerendered HTML.
const DEMO_ROUTE_PATHS: readonly PublicMarketingRoutePath[] = Object.freeze([
  '/demo/practice',
  '/demo/analytics',
]);

const BLOG_ROUTE_BASE_PATH = '/blog' as const;

const BLOG_ARTICLE_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze(
    BLOG_POST_CATALOG_ENTRIES.map(
      entry => entry.routePath as PublicMarketingRoutePath
    )
  );

const PUBLIC_MARKETING_ROUTE_PATHS_SET = new Set<PublicMarketingRoutePath>([
  '/',
  ...PUBLIC_LEVEL_ROUTE_PATHS,
  ...SUBJECT_PAGE_PATHS,
  // AREA_TUITION_ROUTE_PATHS intentionally excluded — see the comment at its
  // declaration above.
  BLOG_ROUTE_BASE_PATH,
  ...BLOG_ARTICLE_ROUTE_PATHS,
  ...LEGAL_ROUTE_PATHS,
  ...TRUST_ROUTE_PATHS,
  ...PARTNERSHIP_WELCOME_ROUTE_PATHS,
]);

const AREA_TUITION_ROUTE_PATHS_SET = new Set<PublicMarketingRoutePath>(
  AREA_TUITION_ROUTE_PATHS
);

const PRIMARY_SCHOOL_DIRECTORY_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([...PRIMARY_HUB_PAGE_PATHS, ...PRIMARY_LEVEL_PATHS, '/psle']);

const SECONDARY_SCHOOL_DIRECTORY_ROUTE_PATHS: readonly PublicMarketingRoutePath[] =
  Object.freeze([
    ...SECONDARY_HUB_PAGE_PATHS,
    ...SECONDARY_HUB_LEVEL_PATHS,
    ...SECONDARY_STREAM_LEVEL_PATHS,
    ...SECONDARY_IB_LEVEL_PATHS,
    ...EXAM_LEVEL_PATHS.filter(routePath => routePath !== '/psle'),
  ]);

const PRIMARY_SCHOOL_DIRECTORY_ROUTE_PATHS_SET =
  new Set<PublicMarketingRoutePath>(PRIMARY_SCHOOL_DIRECTORY_ROUTE_PATHS);

const SECONDARY_SCHOOL_DIRECTORY_ROUTE_PATHS_SET =
  new Set<PublicMarketingRoutePath>(SECONDARY_SCHOOL_DIRECTORY_ROUTE_PATHS);

/**
 * Per-route sitemap priority overrides. Used when a route's intent value
 * differs materially from the default for its bucket — e.g. PSLE is the
 * highest-intent primary-tuition keyword in Singapore and warrants a higher
 * priority than the other exam-prep pages.
 */
const ROUTE_PRIORITY_OVERRIDES: Readonly<Record<string, number>> =
  Object.freeze({
    '/psle': 0.9,
  });

/**
 * Sentinel key in `GENERATED_ROUTE_LAST_MODIFIED` for school pages.
 *
 * All `/schools/{slug}` routes share `SchoolMathPage.tsx` + the school
 * catalog, so one mtime fans out to every school route at resolve time
 * rather than emitting 326 identical entries into the generated map.
 */
const SCHOOL_ROUTE_LAST_MODIFIED_KEY = '/schools/__shared__';

const SCHOOL_ROUTE_PATH_PATTERN = /^\/schools\/[a-z0-9]+(?:-[a-z0-9]+)*$/u;

/**
 * Sentinel key in `GENERATED_ROUTE_LAST_MODIFIED` for area pages.
 *
 * All 15 `/math-tuition/{area}` routes share `AreaMathTuitionPage.tsx`, the
 * area table, and the `src/content/areas/` copy directory, so one mtime fans
 * out to the cluster — same rationale as the school sentinel above.
 */
const AREA_ROUTE_LAST_MODIFIED_KEY = '/math-tuition/__shared__';

/**
 * Sentinel key in `GENERATED_ROUTE_LAST_MODIFIED` for partnership pages.
 *
 * Every partnership route renders one `PartnershipWelcomePage.tsx` over the
 * `src/content/partnerships/` copy directory and the shared guided-signup
 * components, so one mtime fans out to the cluster — same rationale as the
 * school and area sentinels above. The key is namespaced `/partnerships/`
 * even though the routes themselves are root-level: it is a map key, never a
 * URL, and prefixing it keeps the cluster legible next to its two siblings.
 */
const PARTNERSHIP_ROUTE_LAST_MODIFIED_KEY = '/partnerships/__shared__';

function resolveRoutePriority(
  routePath: PublicMarketingRoutePath,
  defaultPriority: number
): number {
  return ROUTE_PRIORITY_OVERRIDES[routePath] ?? defaultPriority;
}

/**
 * Resolve a route's lastmod from the build-time-generated git mtime map.
 *
 * Routes with no generated entry fall back to the supplied default (e.g.
 * blog frontmatter dates) and ultimately to `null`, in which case
 * `sitemap.xml` omits the `<lastmod>` element entirely. This keeps
 * uniform fake-fresh dates out of the sitemap, which Google discounts
 * as a noise signal.
 */
function resolveRouteLastModified(
  routePath: PublicMarketingRoutePath,
  fallback: string | null
): string | null {
  if (SCHOOL_ROUTE_PATH_PATTERN.test(routePath)) {
    return (
      GENERATED_ROUTE_LAST_MODIFIED[SCHOOL_ROUTE_LAST_MODIFIED_KEY] ?? fallback
    );
  }
  if (AREA_TUITION_ROUTE_PATHS_SET.has(routePath)) {
    return (
      GENERATED_ROUTE_LAST_MODIFIED[AREA_ROUTE_LAST_MODIFIED_KEY] ?? fallback
    );
  }
  if (isPartnershipRoutePath(routePath)) {
    return (
      GENERATED_ROUTE_LAST_MODIFIED[PARTNERSHIP_ROUTE_LAST_MODIFIED_KEY] ??
      fallback
    );
  }
  return GENERATED_ROUTE_LAST_MODIFIED[routePath] ?? fallback;
}

function normalizeToBasePath(routePath: string): string {
  // Express `req.path` should already be query-less, but keep this robust.
  const withoutQuery = String(routePath).split('#')[0].split('?')[0];
  return withoutQuery.trim();
}

/**
 * Normalize a route path so prerender/sitemap lookups can be deterministic.
 *
 * @param routePath
 * @returns Canonical route path starting with `/`, or empty string for invalid input.
 */
export function normalizePublicMarketingRoutePath(routePath: string): string;
export function normalizePublicMarketingRoutePath(routePath: unknown): string;
export function normalizePublicMarketingRoutePath(routePath: unknown): string {
  if (typeof routePath !== 'string') return '';

  let normalized = normalizeToBasePath(routePath);
  if (!normalized) return '';

  if (!normalized.startsWith('/')) normalized = `/${normalized}`;

  // Remove trailing slashes (except root).
  if (normalized.length > 1) normalized = normalized.replace(/\/+$/u, '');

  // Blog slugs are case-insensitive in input; canonicalize to lowercase.
  if (normalized.startsWith(`${BLOG_ROUTE_BASE_PATH}/`)) {
    const slug = normalized.slice(`${BLOG_ROUTE_BASE_PATH}/`.length);
    normalized = `${BLOG_ROUTE_BASE_PATH}/${slug.toLowerCase()}`;
  }

  return normalized;
}

/**
 * Check whether a normalized route path is eligible for sitemap/prerendering.
 *
 * School paths are checked via prefix pattern rather than Set membership to
 * avoid adding ~326 strings to the client bundle.
 *
 * @param routePath Canonical route path (preferred: use `normalizePublicMarketingRoutePath` first).
 */
export function isPublicMarketingRoutePath(routePath: string): boolean;
export function isPublicMarketingRoutePath(routePath: unknown): boolean;
export function isPublicMarketingRoutePath(routePath: unknown): boolean {
  if (typeof routePath !== 'string') return false;
  return (
    PUBLIC_MARKETING_ROUTE_PATHS_SET.has(
      routePath as PublicMarketingRoutePath
    ) || SCHOOL_ROUTE_PATH_PATTERN.test(routePath)
  );
}

/**
 * Classify public marketing routes that need prerendered school directory data.
 *
 * Pages with school directories hydrate synchronously from an injected JSON
 * payload, so the prerender pipeline needs a stable route-to-directory mapping.
 */
export function getPrerenderSchoolDirectoryType(
  routePath: string
): PrerenderSchoolDirectoryType | null;
export function getPrerenderSchoolDirectoryType(
  routePath: unknown
): PrerenderSchoolDirectoryType | null;
export function getPrerenderSchoolDirectoryType(
  routePath: unknown
): PrerenderSchoolDirectoryType | null {
  const normalizedRoutePath = normalizePublicMarketingRoutePath(routePath);

  if (!normalizedRoutePath) {
    return null;
  }

  if (
    AREA_TUITION_ROUTE_PATHS_SET.has(
      normalizedRoutePath as PublicMarketingRoutePath
    )
  ) {
    return 'area';
  }

  if (
    PRIMARY_SCHOOL_DIRECTORY_ROUTE_PATHS_SET.has(
      normalizedRoutePath as PublicMarketingRoutePath
    )
  ) {
    return 'primary';
  }

  if (
    SECONDARY_SCHOOL_DIRECTORY_ROUTE_PATHS_SET.has(
      normalizedRoutePath as PublicMarketingRoutePath
    )
  ) {
    return 'secondary';
  }

  return null;
}

function resolveLastModified({
  blogEntry,
  defaultLastModified,
}: {
  blogEntry?: BlogCatalogEntry;
  defaultLastModified: string | null;
}): string | null {
  if (blogEntry?.updatedDate) return blogEntry.updatedDate;
  if (blogEntry?.publishDate) return blogEntry.publishDate;
  return defaultLastModified ?? null;
}

function toMaybeLastModified(value?: string): string | null {
  const asString = typeof value === 'string' ? value.trim() : '';
  return asString ? asString : null;
}

/**
 * List all public marketing route entries for `sitemap.xml` generation
 * and `scripts/prerender-marketing.ts` crawling.
 */
export function listPublicMarketingRouteEntries({
  defaultLastModified,
  schoolRoutePaths = [],
}: {
  defaultLastModified?: string;
  schoolRoutePaths?: readonly string[];
} = {}): PublicMarketingRouteEntry[] {
  const lastModified = toMaybeLastModified(defaultLastModified);

  const entries: PublicMarketingRouteEntry[] = [];

  const pushEntry = (
    routePath: PublicMarketingRoutePath,
    defaultPriority: number,
    fallbackLastModified: string | null = lastModified,
    prerender = true
  ) => {
    entries.push({
      routePath,
      priority: resolveRoutePriority(routePath, defaultPriority),
      lastModified: resolveRouteLastModified(routePath, fallbackLastModified),
      prerender,
    });
  };

  // 1. Homepage
  pushEntry('/', 1.0);

  for (const page of SUBJECT_PAGES) {
    pushEntry(page.path, page.hub ? 0.78 : 0.7);
  }

  // 2. Level/subject landing pages
  for (const routePath of PRIMARY_HUB_PAGE_PATHS) {
    pushEntry(routePath, 0.85);
  }
  for (const routePath of PRIMARY_LEVEL_PATHS) {
    pushEntry(routePath, 0.8);
  }
  for (const routePath of SECONDARY_HUB_PAGE_PATHS) {
    pushEntry(routePath, 0.78);
  }
  for (const routePath of SECONDARY_HUB_LEVEL_PATHS) {
    pushEntry(routePath, 0.75);
  }
  for (const routePath of SECONDARY_STREAM_LEVEL_PATHS) {
    pushEntry(routePath, 0.7);
  }
  for (const routePath of SECONDARY_IB_LEVEL_PATHS) {
    pushEntry(routePath, 0.7);
  }
  for (const routePath of EXAM_LEVEL_PATHS) {
    pushEntry(routePath, 0.6);
  }
  for (const routePath of ONLINE_TUITION_PILLAR_PATHS) {
    pushEntry(routePath, 0.85);
  }
  for (const routePath of ONLINE_TUITION_SPOKE_PATHS) {
    pushEntry(routePath, 0.78);
  }
  for (const routePath of ONLINE_TUITION_COMPARISON_PATHS) {
    pushEntry(routePath, 0.7);
  }

  // Area pages withheld from the sitemap — see the comment at
  // AREA_TUITION_ROUTE_PATHS's declaration above.
  for (const routePath of SYLLABUS_HUB_ROUTE_PATHS) {
    pushEntry(routePath, 0.7);
  }

  // 3. Blog listing + blog articles
  pushEntry(BLOG_ROUTE_BASE_PATH, 0.7);

  for (const blogEntry of BLOG_POST_CATALOG_ENTRIES) {
    entries.push({
      routePath: blogEntry.routePath,
      priority: resolveRoutePriority(blogEntry.routePath, 0.6),
      lastModified: resolveRouteLastModified(
        blogEntry.routePath,
        resolveLastModified({
          blogEntry,
          defaultLastModified: lastModified,
        })
      ),
      prerender: true,
    });
  }

  // 4. Trust pages (about-us / contact) — E-E-A-T signals for Google.
  // Higher priority than legal pages because Google weighs them for entity trust.
  for (const routePath of TRUST_ROUTE_PATHS) {
    pushEntry(routePath, 0.6);
  }

  // 5. Partnership welcome pages. Real, indexable pages — but they are welcome
  //    mats for people who already hold an access code, not search targets, so
  //    they sit below the trust pages and above the legal ones. Nobody searches
  //    for these; they are linked to.
  for (const routePath of PARTNERSHIP_WELCOME_ROUTE_PATHS) {
    pushEntry(routePath, 0.4);
  }

  // 6. Legal pages
  for (const routePath of LEGAL_ROUTE_PATHS) {
    pushEntry(routePath, 0.35);
  }

  // 7. School SEO pages (~326 pages, one per primary/secondary school)
  for (const routePath of schoolRoutePaths) {
    pushEntry(routePath as PublicMarketingRoutePath, 0.5);
  }

  // 8. Interactive demo pages — sitemap-only (see DEMO_ROUTE_PATHS). Lowest
  //    priority (0.1) so they never compete with marketing/level/school pages
  //    for crawl attention, and `prerender: false` so the build-time crawl and
  //    the prerender route registry skip them. Real lastmod still resolves from
  //    the generated mtime map (ROUTE_SOURCE_FILES has demo entries).
  for (const routePath of DEMO_ROUTE_PATHS) {
    pushEntry(routePath, 0.1, null, false);
  }

  return entries;
}
